// @ts-nocheck
import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Plus, Search, ArrowUpDown, Eye, Edit, Trash2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { usePermissions } from '@/hooks/usePermissions';
import { useOrgPermissions } from '@/hooks/useOrgPermissions';
import { useToast } from '@/hooks/use-toast';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

interface TipoContrato {
  id: string;
  nome: string;
  descricao?: string;
  ativo: boolean;
  created_at: string;
  updated_at: string;
  created_by?: string;
  updated_by?: string;
}

export default function TipoContrato() {
  const { user } = useAuth();
  const { hasPermission } = usePermissions();
  const { userOrgInfo } = useOrgPermissions();
  const { toast } = useToast();

  const [tipos, setTipos] = useState<TipoContrato[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortColumn, setSortColumn] = useState<keyof TipoContrato>('nome');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showViewDialog, setShowViewDialog] = useState(false);
  const [selectedTipo, setSelectedTipo] = useState<TipoContrato | null>(null);
  const [formData, setFormData] = useState({
    nome: '',
    descricao: ''
  });

  const canRead = hasPermission('recursos_humanos', 'tipo_contrato', 'read');
  const canWrite = hasPermission('recursos_humanos', 'tipo_contrato', 'write');
  const canAdmin = hasPermission('recursos_humanos', 'tipo_contrato', 'admin');

  useEffect(() => {
    if (canRead) {
      fetchTipos();
    } else {
      setLoading(false);
    }
  }, [canRead, sortColumn, sortDirection]);

  const fetchTipos = async () => {
    try {
      setLoading(true);
      let query = supabase
        .from('tipo_contrato')
        .select('*')
        .eq('org_id', userOrgInfo.orgId); // Filtrar apenas pela organização do usuário

      if (searchQuery) {
        query = query.or(`nome.ilike.%${searchQuery}%,descricao.ilike.%${searchQuery}%`);
      }

      query = query.order(sortColumn, { ascending: sortDirection === 'asc' });

      const { data, error } = await query;

      if (error) throw error;
      setTipos(data || []);
    } catch (error) {
      console.error('Erro ao buscar tipos de contrato:', error);
      toast({
        title: 'Erro',
        description: 'Erro ao carregar tipos de contrato',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (column: keyof TipoContrato) => {
    if (sortColumn === column) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const handleSearch = () => {
    fetchTipos();
  };

  const handleCreate = async () => {
    try {
      if (!formData.nome.trim()) {
        toast({
          title: 'Erro',
          description: 'Nome é obrigatório',
          variant: 'destructive'
        });
        return;
      }

      const { error } = await supabase
        .from('tipo_contrato')
        .insert([{
          nome: formData.nome.trim(),
          descricao: formData.descricao.trim() || null,
          org_id: userOrgInfo.orgId,
          created_by: user?.id
        }]);

      if (error) throw error;

      toast({
        title: 'Sucesso',
        description: 'Tipo de contrato criado com sucesso'
      });

      setShowCreateDialog(false);
      setFormData({ nome: '', descricao: '' });
      fetchTipos();
    } catch (error) {
      console.error('Erro ao criar tipo de contrato:', error);
      toast({
        title: 'Erro',
        description: 'Erro ao criar tipo de contrato',
        variant: 'destructive'
      });
    }
  };

  const handleEdit = async () => {
    try {
      if (!formData.nome.trim()) {
        toast({
          title: 'Erro',
          description: 'Nome é obrigatório',
          variant: 'destructive'
        });
        return;
      }

      if (!selectedTipo) return;

      const { error } = await supabase
        .from('tipo_contrato')
        .update({
          nome: formData.nome.trim(),
          descricao: formData.descricao.trim() || null,
          updated_by: user?.id
        })
        .eq('id', selectedTipo.id);

      if (error) throw error;

      toast({
        title: 'Sucesso',
        description: 'Tipo de contrato atualizado com sucesso'
      });

      setShowEditDialog(false);
      setFormData({ nome: '', descricao: '' });
      setSelectedTipo(null);
      fetchTipos();
    } catch (error) {
      console.error('Erro ao atualizar tipo de contrato:', error);
      toast({
        title: 'Erro',
        description: 'Erro ao atualizar tipo de contrato',
        variant: 'destructive'
      });
    }
  };

  const handleDelete = async (id: string) => {
    try {
      const { error } = await supabase
        .from('tipo_contrato')
        .delete()
        .eq('id', id);

      if (error) throw error;

      toast({
        title: 'Sucesso',
        description: 'Tipo de contrato excluído com sucesso'
      });

      fetchTipos();
    } catch (error) {
      console.error('Erro ao excluir tipo de contrato:', error);
      toast({
        title: 'Erro',
        description: 'Erro ao excluir tipo de contrato',
        variant: 'destructive'
      });
    }
  };

  if (!canRead) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-muted-foreground">Você não tem permissão para visualizar tipos de contrato.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold">Tipos de Contrato</h1>
        {canWrite && (
          <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
            <DialogTrigger asChild>
              <Button>
                <Plus className="mr-2 h-4 w-4" />
                Novo Tipo
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Criar Novo Tipo de Contrato</DialogTitle>
                <DialogDescription>
                  Preencha as informações para criar um novo tipo de contrato.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="nome">Nome *</Label>
                  <Input
                    id="nome"
                    value={formData.nome}
                    onChange={(e) => setFormData(prev => ({ ...prev, nome: e.target.value }))}
                    placeholder="Digite o nome do tipo de contrato"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="descricao">Descrição</Label>
                  <Textarea
                    id="descricao"
                    value={formData.descricao}
                    onChange={(e) => setFormData(prev => ({ ...prev, descricao: e.target.value }))}
                    placeholder="Digite uma descrição opcional"
                    rows={3}
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
                  Cancelar
                </Button>
                <Button onClick={handleCreate}>
                  Criar
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Pesquisar e Filtrar</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex gap-4">
            <div className="flex-1">
              <Input
                placeholder="Pesquisar por nome ou descrição..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              />
            </div>
            <Button onClick={handleSearch}>
              <Search className="mr-2 h-4 w-4" />
              Pesquisar
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Tipos de Contrato Cadastrados</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-4">
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : tipos.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-muted-foreground">Nenhum tipo de contrato encontrado.</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead 
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => handleSort('nome')}
                  >
                    <div className="flex items-center">
                      Nome
                      <ArrowUpDown className="ml-2 h-4 w-4" />
                    </div>
                  </TableHead>
                  <TableHead 
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => handleSort('descricao')}
                  >
                    <div className="flex items-center">
                      Descrição
                      <ArrowUpDown className="ml-2 h-4 w-4" />
                    </div>
                  </TableHead>
                  <TableHead 
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => handleSort('ativo')}
                  >
                    <div className="flex items-center">
                      Status
                      <ArrowUpDown className="ml-2 h-4 w-4" />
                    </div>
                  </TableHead>
                  <TableHead 
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => handleSort('created_at')}
                  >
                    <div className="flex items-center">
                      Criado em
                      <ArrowUpDown className="ml-2 h-4 w-4" />
                    </div>
                  </TableHead>
                  <TableHead 
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => handleSort('updated_at')}
                  >
                    <div className="flex items-center">
                      Atualizado em
                      <ArrowUpDown className="ml-2 h-4 w-4" />
                    </div>
                  </TableHead>
                  <TableHead>Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {tipos.map((tipo) => (
                  <TableRow key={tipo.id}>
                    <TableCell className="font-medium">{tipo.nome}</TableCell>
                    <TableCell className="max-w-xs truncate">{tipo.descricao || '-'}</TableCell>
                    <TableCell>
                      <Badge variant={tipo.ativo ? 'default' : 'secondary'}>
                        {tipo.ativo ? 'Ativo' : 'Inativo'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {format(new Date(tipo.created_at), 'dd/MM/yyyy HH:mm', { locale: ptBR })}
                    </TableCell>
                    <TableCell>
                      {format(new Date(tipo.updated_at), 'dd/MM/yyyy HH:mm', { locale: ptBR })}
                    </TableCell>
                    <TableCell>
                      <div className="flex space-x-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setSelectedTipo(tipo);
                            setShowViewDialog(true);
                          }}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        {canWrite && (
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => {
                              setSelectedTipo(tipo);
                              setFormData({ nome: tipo.nome, descricao: tipo.descricao || '' });
                              setShowEditDialog(true);
                            }}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                        )}
                        {canAdmin && (
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button variant="outline" size="sm">
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Confirmar Exclusão</AlertDialogTitle>
                                <AlertDialogDescription>
                                  Tem certeza que deseja excluir o tipo de contrato "{tipo.nome}"? Esta ação não pode ser desfeita.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancelar</AlertDialogCancel>
                                <AlertDialogAction 
                                  onClick={() => handleDelete(tipo.id)}
                                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                >
                                  Excluir
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Editar Tipo de Contrato</DialogTitle>
            <DialogDescription>
              Atualize as informações do tipo de contrato.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="edit-nome">Nome *</Label>
              <Input
                id="edit-nome"
                value={formData.nome}
                onChange={(e) => setFormData(prev => ({ ...prev, nome: e.target.value }))}
                placeholder="Digite o nome do tipo de contrato"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-descricao">Descrição</Label>
              <Textarea
                id="edit-descricao"
                value={formData.descricao}
                onChange={(e) => setFormData(prev => ({ ...prev, descricao: e.target.value }))}
                placeholder="Digite uma descrição opcional"
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowEditDialog(false);
              setFormData({ nome: '', descricao: '' });
              setSelectedTipo(null);
            }}>
              Cancelar
            </Button>
            <Button onClick={handleEdit}>
              Salvar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showViewDialog} onOpenChange={setShowViewDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Detalhes do Tipo de Contrato</DialogTitle>
          </DialogHeader>
          {selectedTipo && (
            <div className="space-y-4">
              <div>
                <Label>Nome</Label>
                <p className="text-sm text-muted-foreground mt-1">{selectedTipo.nome}</p>
              </div>
              <div>
                <Label>Descrição</Label>
                <p className="text-sm text-muted-foreground mt-1">{selectedTipo.descricao || 'Sem descrição'}</p>
              </div>
              <div>
                <Label>Status</Label>
                <p className="text-sm text-muted-foreground mt-1">
                  <Badge variant={selectedTipo.ativo ? 'default' : 'secondary'}>
                    {selectedTipo.ativo ? 'Ativo' : 'Inativo'}
                  </Badge>
                </p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Criado em</Label>
                  <p className="text-sm text-muted-foreground mt-1">
                    {format(new Date(selectedTipo.created_at), 'dd/MM/yyyy HH:mm', { locale: ptBR })}
                  </p>
                </div>
                <div>
                  <Label>Atualizado em</Label>
                  <p className="text-sm text-muted-foreground mt-1">
                    {format(new Date(selectedTipo.updated_at), 'dd/MM/yyyy HH:mm', { locale: ptBR })}
                  </p>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowViewDialog(false)}>
              Fechar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}